import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Facebook, Instagram, LogIn, LogOut, Menu, X } from 'lucide-react';

const Navigation = ({ user, onLogout, settings = {}, isLanding = true }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 100);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    setMobileOpen(false);
    if (!isLanding) {
      navigate('/');
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 300);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const allNavItems = [
    { key: 'home', label: 'Home', action: () => scrollTo('home') },
    { key: 'about', label: 'Über uns', action: () => scrollTo('about') },
    { key: 'queens', label: 'Katzen', action: () => scrollTo('queens') },
    { key: 'studs', label: 'Kater', action: () => scrollTo('studs') },
    { key: 'kitten', label: 'Kitten', action: () => { setMobileOpen(false); window.dispatchEvent(new CustomEvent('open-kitten-popup')); }},
    { key: 'ratgeber', label: 'Ratgeber', action: () => { setMobileOpen(false); navigate('/ratgeber'); }},
    { key: 'gallery', label: 'Galerie', action: () => scrollTo('gallery') },
    { key: 'reviews', label: 'Kundenstimmen', action: () => scrollTo('reviews') },
    { key: 'contact', label: 'Anfrage', action: () => scrollTo('contact') },
  ];

  const hiddenItems = (settings.nav_hidden_items || '').split(',').filter(Boolean);
  const orderStr = settings.nav_order || '';
  let navLinks = allNavItems.filter(item => {
    if (hiddenItems.includes(item.key)) return false;
    if (item.key === 'kitten' && settings.kitten_mode_active !== 'true') return false;
    return true;
  });
  if (orderStr) {
    const keys = orderStr.split(',').filter(Boolean);
    const ordered = [];
    keys.forEach(k => { const item = navLinks.find(n => n.key === k); if (item) ordered.push(item); });
    navLinks.forEach(item => { if (!ordered.find(o => o.key === item.key)) ordered.push(item); });
    navLinks = ordered;
  }

  const navBg = settings.nav_bg_color || '#2E3A46';
  const navText = settings.nav_text_color || '#C5D0DA';
  const navSize = settings.nav_font_size || '18';

  return (
    <nav data-testid="main-navigation"
      className={`sticky top-0 z-50 transition-shadow duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
      style={{ backgroundColor: navBg }}>

      {/* Desktop */}
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
        <div className="hidden lg:flex items-center h-16 gap-4">
          <div className="flex items-center gap-3 shrink-0">
            <Link to="/" className="hover:opacity-80 transition-opacity">
              <img src={`${window.location.origin}/favicon.png`} alt="Schneeblumen" className="w-7 h-7" />
            </Link>
            {settings.facebook_url && <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer" style={{color: navText}} className="hover:opacity-70"><Facebook size={17} /></a>}
            {settings.instagram_url && <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" style={{color: navText}} className="hover:opacity-70"><Instagram size={17} /></a>}
          </div>
          <div className="flex-1 flex items-center justify-center gap-1">
            {navLinks.map((link) => (
              <button key={link.key} onClick={link.action}
                className="hover:opacity-100 px-4 py-2 font-body transition-all rounded-lg hover:bg-white/10 whitespace-nowrap"
                style={{ color: navText, fontSize: `${navSize}px` }}>
                {link.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {user ? (
              <>
                {user.role === 'admin' && <Link to="/admin" style={{color: navText}} className="hover:opacity-80 text-sm px-3 py-1.5 rounded-lg hover:bg-white/10">Admin</Link>}
                {user.role === 'buyer' && <Link to="/dashboard" style={{color: navText}} className="hover:opacity-80 text-sm px-3 py-1.5 rounded-lg hover:bg-white/10">Mein Bereich</Link>}
                <button onClick={onLogout} data-testid="logout-button" className="flex items-center gap-1.5 text-white text-sm px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20"><LogOut size={16} /> Abmelden</button>
              </>
            ) : (
              <Link to="/login" data-testid="login-button" className="flex items-center gap-1.5 text-white bg-[#7A9BB5] hover:bg-[#6889a3] px-4 py-1.5 rounded-xl text-sm"><LogIn size={16} /> Anmelden</Link>
            )}
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="lg:hidden">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center gap-4">
            <Link to="/"><img src={`${window.location.origin}/favicon.png`} alt="" className="w-9 h-9" /></Link>
            {settings.facebook_url && <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer" style={{color: navText}} className="opacity-70"><Facebook size={22} /></a>}
            {settings.instagram_url && <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" style={{color: navText}} className="opacity-70"><Instagram size={22} /></a>}
          </div>
          <button onClick={() => setMobileOpen(!mobileOpen)} style={{color: navText}} className="p-3 rounded-lg hover:bg-white/10">
            {mobileOpen ? <X size={30} /> : <Menu size={30} />}
          </button>
        </div>

        {/* Mobile Dropdown */}
        {mobileOpen && (
          <div className="animate-fade-in pb-5 px-3 border-t border-white/10">
            {navLinks.map((link) => (
              <button key={link.key} onClick={link.action}
                className="block w-full text-left px-5 py-4 rounded-xl hover:bg-white/10 transition-colors font-body text-lg"
                style={{ color: navText }}>
                {link.label}
              </button>
            ))}
            <div className="border-t border-white/10 mt-3 pt-4 px-4">
              {user ? (
                <div className="flex flex-col gap-3">
                  {user.role === 'admin' && <Link to="/admin" onClick={() => setMobileOpen(false)} style={{color: navText}} className="text-base py-3 px-2">Admin-Panel</Link>}
                  {user.role === 'buyer' && <Link to="/dashboard" onClick={() => setMobileOpen(false)} style={{color: navText}} className="text-base py-3 px-2">Mein Bereich</Link>}
                  <button onClick={() => { onLogout(); setMobileOpen(false); }} className="flex items-center gap-2 text-base py-3 px-2 text-red-300"><LogOut size={20} /> Abmelden</button>
                </div>
              ) : (
                <Link to="/login" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 text-white bg-[#7A9BB5] px-6 py-4 rounded-xl text-lg justify-center font-medium"><LogIn size={20} /> Anmelden</Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
